import React, { useEffect } from 'react';
import { NAVIERA_DIAGNOSTIC_CONFIG } from '../../config/questionnaire';

export const createEmptyNavieraDiagnostic = (name = '') => ({
  name,
  otherName: '',
  volumeShare: '',
  route: '',
  scores: {},
  problems: [],
  otherProblem: '',
  incidentCount: '',
  severity: '',
  impactDays: '',
  costImpact: '',
  resolved: '',
  comment: '',
});

export const normalizeDiagnosticList = (value) => (Array.isArray(value) ? value : []);

export const getDiagnosticCarrierName = (detail = {}) => {
  const selected = detail.name?.toString().trim();
  if (!selected) return 'Naviera sin seleccionar';
  if (selected === 'Otra') return detail.otherName?.toString().trim() || 'Otra naviera';
  return selected;
};

export const formatNavieraDiagnostics = (value) => {
  const diagnostics = normalizeDiagnosticList(value);
  if (!diagnostics.length) return '(Sin respuesta)';

  return diagnostics.map((detail, index) => {
    const scores = detail.scores || {};
    const scoreSummary = NAVIERA_DIAGNOSTIC_CONFIG.criteria
      .map((criterion) => `${criterion.label}: ${scores[criterion.id] || 'N/D'}`)
      .join(' · ');
    const problems = detail.problems?.length ? detail.problems.join(', ') : 'Sin problemas registrados';
    return `${index + 1}. ${getDiagnosticCarrierName(detail)} (${detail.volumeShare || 'volumen N/D'}). ${scoreSummary}. Problemas: ${problems}. Severidad: ${detail.severity || 'N/D'}. Resolución: ${detail.resolved || 'N/D'}.`;
  }).join('\n');
};

export const resolvePrimaryCarrier = (formData = {}, fallback = '') => {
  const selected = formData.meta_naviera_principal || fallback;
  if (selected !== 'Otra') return selected || '';
  const diagnostics = normalizeDiagnosticList(formData.meta_naviera_detalle);
  const otherName = diagnostics.find((detail) => detail.name === 'Otra')?.otherName?.toString().trim();
  return otherName || selected;
};

export const validateNavieraDiagnostics = (value, config = NAVIERA_DIAGNOSTIC_CONFIG) => {
  const diagnostics = normalizeDiagnosticList(value);
  if (!diagnostics.length) {
    return 'Evalúa al menos una naviera para construir rankings y coyunturas.';
  }

  const hasIncomplete = diagnostics.some((detail) => {
    const carrierName = detail.name?.toString().trim();
    if (!carrierName) return true;
    if (carrierName === 'Otra' && !detail.otherName?.toString().trim()) return true;
    if (!detail.volumeShare) return true;
    if (!detail.incidentCount || !detail.severity || !detail.impactDays || !detail.costImpact || !detail.resolved) return true;
    return config.criteria.some((criterion) => !detail.scores?.[criterion.id]);
  });

  return hasIncomplete ? 'Completa naviera, volumen, calificaciones, impacto y resolución en cada tarjeta.' : '';
};

const CARRIER_ACCENT_COLORS = ['#004AAD', '#0EA5E9', '#8B5CF6'];

const SCORE_LABELS = ['', 'Muy mala', 'Mala', 'Aceptable', 'Buena', 'Excelente'];

export const NavieraDiagnostics = ({ question, value, onChange, error, primaryCarrier }) => {
  const config = question.config || NAVIERA_DIAGNOSTIC_CONFIG;
  const diagnostics = normalizeDiagnosticList(value);

  useEffect(() => {
    if (!primaryCarrier || diagnostics.length) return;
    onChange(question.id, [createEmptyNavieraDiagnostic(primaryCarrier)]);
  }, [diagnostics.length, onChange, primaryCarrier, question.id]);

  const updateDiagnostic = (index, patch) => {
    const next = diagnostics.map((item, itemIndex) => (
      itemIndex === index ? { ...item, ...patch } : item
    ));
    onChange(question.id, next);
  };

  const updateScore = (index, criterionId, score) => {
    const current = diagnostics[index] || createEmptyNavieraDiagnostic();
    updateDiagnostic(index, {
      scores: {
        ...(current.scores || {}),
        [criterionId]: score,
      },
    });
  };

  const toggleProblem = (index, problem) => {
    const current = diagnostics[index] || createEmptyNavieraDiagnostic();
    const problems = Array.isArray(current.problems) ? current.problems : [];
    let nextProblems;
    if (problem === 'Sin problemas relevantes') {
      nextProblems = problems.includes(problem) ? [] : [problem];
    } else {
      const withoutNoProblem = problems.filter((item) => item !== 'Sin problemas relevantes');
      nextProblems = withoutNoProblem.includes(problem)
        ? withoutNoProblem.filter((item) => item !== problem)
        : [...withoutNoProblem, problem];
    }
    updateDiagnostic(index, { problems: nextProblems });
  };

  const addDiagnostic = () => {
    if (diagnostics.length >= config.maxCarriers) return;
    onChange(question.id, [...diagnostics, createEmptyNavieraDiagnostic()]);
  };

  const removeDiagnostic = (index) => {
    onChange(question.id, diagnostics.filter((_, itemIndex) => itemIndex !== index));
  };

  return (
    <div className={`space-y-4 rounded-2xl border ${error ? 'border-rose-300 bg-rose-50/40' : 'border-slate-200/50 bg-white/95'} p-4 shadow-card md:p-5`}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <label className="block text-base font-semibold text-slate-900">{question.text}</label>
          <p className="mt-1 text-sm text-slate-500">Esta sección permite comparar desempeño, fricciones e impacto por naviera.</p>
        </div>
        <button
          type="button"
          onClick={addDiagnostic}
          disabled={diagnostics.length >= config.maxCarriers}
          className="flex items-center gap-1.5 rounded-xl border border-brand-blue/30 bg-brand-blue/5 px-3.5 py-2 text-xs font-semibold text-brand-blue transition-all duration-200 hover:bg-brand-blue/10 hover:shadow-sm disabled:opacity-40"
        >
          <span className="text-base leading-none">+</span> Agregar naviera
        </button>
      </div>

      <div className="space-y-4">
        {diagnostics.map((detail, index) => {
          const accentColor = CARRIER_ACCENT_COLORS[index % CARRIER_ACCENT_COLORS.length];
          const carrierName = getDiagnosticCarrierName(detail);
          const completedScores = config.criteria.filter(c => detail.scores?.[c.id]).length;

          return (
            <div key={`${detail.name || 'naviera'}_${index}`} className="rounded-2xl border border-slate-200/70 bg-white overflow-hidden shadow-sm">
              {/* Accent strip */}
              <div className="h-[3px]" style={{ backgroundColor: accentColor }} />

              {/* Header */}
              <div className="px-4 pt-4 pb-3 flex items-center justify-between gap-3 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <span
                    className="flex h-8 w-8 items-center justify-center rounded-lg text-white text-xs font-bold"
                    style={{ backgroundColor: accentColor }}
                  >
                    {index + 1}
                  </span>
                  <div>
                    <p className="text-sm font-bold text-slate-900">{carrierName}</p>
                    <p className="text-[0.6rem] text-slate-400">{completedScores}/{config.criteria.length} criterios evaluados</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeDiagnostic(index)}
                  disabled={diagnostics.length === 1}
                  className="flex h-7 w-7 items-center justify-center rounded-lg border border-slate-200 text-slate-400 text-sm hover:bg-rose-50 hover:text-rose-500 hover:border-rose-200 transition-all duration-200 disabled:opacity-40"
                  title="Quitar naviera"
                >
                  ✕
                </button>
              </div>

              <div className="p-4 space-y-5">
                {/* Identity fields */}
                <div className="grid gap-3 lg:grid-cols-3">
                  <label className="text-xs font-semibold uppercase text-slate-400">
                    Naviera
                    <select
                      value={detail.name || ''}
                      onChange={(event) => updateDiagnostic(index, {
                        name: event.target.value,
                        otherName: event.target.value === 'Otra' ? detail.otherName : '',
                      })}
                      className="mt-1 block w-full rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#004AAD]/25 focus:border-[#004AAD]/40 transition-all"
                    >
                      <option value="">Selecciona</option>
                      {config.carrierOptions.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </label>
                  <label className="text-xs font-semibold uppercase text-slate-400">
                    Peso operativo
                    <select
                      value={detail.volumeShare || ''}
                      onChange={(event) => updateDiagnostic(index, { volumeShare: event.target.value })}
                      className="mt-1 block w-full rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#004AAD]/25 focus:border-[#004AAD]/40 transition-all"
                    >
                      <option value="">Selecciona</option>
                      {config.volumeOptions.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </label>
                  <label className="text-xs font-semibold uppercase text-slate-400">
                    Ruta / destino
                    <input
                      type="text"
                      value={detail.route || ''}
                      onChange={(event) => updateDiagnostic(index, { route: event.target.value })}
                      className="mt-1 block w-full rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-2.5 text-sm text-slate-800 placeholder:italic placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#004AAD]/25 focus:border-[#004AAD]/40 transition-all"
                      placeholder="Ej. Asia, Europa, Costa Oeste"
                    />
                  </label>
                </div>

                {detail.name === 'Otra' && (
                  <label className="block text-xs font-semibold uppercase text-slate-400">
                    Nombre de la naviera
                    <input
                      type="text"
                      value={detail.otherName || ''}
                      onChange={(event) => updateDiagnostic(index, { otherName: event.target.value })}
                      className="mt-1 block w-full rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#004AAD]/25 focus:border-[#004AAD]/40 transition-all"
                    />
                  </label>
                )}

                {/* Evaluation scores */}
                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase text-slate-400">Evaluación 1–5</p>
                  <div className="grid gap-2">
                    {config.criteria.map((criterion) => (
                      <div key={criterion.id} className="grid gap-2 rounded-xl bg-slate-50/80 p-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
                        <p className="text-sm font-medium text-slate-700">{criterion.label}</p>
                        <div className="grid grid-cols-5 gap-1.5">
                          {[1, 2, 3, 4, 5].map((score) => {
                            const selected = detail.scores?.[criterion.id] === score.toString();
                            return (
                              <button
                                key={score}
                                type="button"
                                onClick={() => updateScore(index, criterion.id, score.toString())}
                                className={`flex flex-col items-center gap-0.5 rounded-xl border px-1 py-1.5 transition-all duration-200 ${
                                  selected
                                    ? 'border-brand-blue bg-brand-blue text-white shadow-sm scale-105'
                                    : 'border-slate-200 bg-white text-slate-500 hover:border-brand-blue/40 hover:shadow-sm'
                                }`}
                                title={SCORE_LABELS[score]}
                              >
                                <span className="text-xs font-bold">{score}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Problems */}
                <div className="space-y-3">
                  <p className="text-xs font-semibold uppercase text-slate-400">Problemas detectados</p>
                  <div className="flex flex-wrap gap-2">
                    {config.problemOptions.map((problem) => {
                      const selected = detail.problems?.includes(problem);
                      const isPositive = problem === 'Sin problemas relevantes';
                      return (
                        <button
                          key={problem}
                          type="button"
                          onClick={() => toggleProblem(index, problem)}
                          className={`flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-xs font-semibold transition-all duration-200 ${
                            selected
                              ? isPositive
                                ? 'border-emerald-400 bg-emerald-50 text-emerald-700 ring-2 ring-emerald-200'
                                : 'border-brand-blue bg-brand-blue/10 text-brand-blue ring-2 ring-brand-blue/20'
                              : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:shadow-sm'
                          }`}
                        >
                          <span className="text-[0.65rem]">{selected ? (isPositive ? '✓' : '⚠') : '○'}</span>
                          {problem}
                        </button>
                      );
                    })}
                  </div>
                  {detail.problems?.includes('Otro') && (
                    <input
                      type="text"
                      value={detail.otherProblem || ''}
                      onChange={(event) => updateDiagnostic(index, { otherProblem: event.target.value })}
                      className="block w-full rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-2.5 text-sm text-slate-800 placeholder:italic placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#004AAD]/25 focus:border-[#004AAD]/40 transition-all"
                      placeholder="Describe el problema"
                    />
                  )}
                </div>

                {/* Impact grid */}
                <div className="rounded-xl bg-slate-50/60 border border-slate-100 p-3.5">
                  <p className="text-xs font-semibold uppercase text-slate-400 mb-3">Impacto operativo</p>
                  <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
                    {[
                      ['Incidentes', 'incidentCount', config.incidentOptions],
                      ['Severidad', 'severity', config.severityOptions],
                      ['Impacto días', 'impactDays', config.impactDaysOptions],
                      ['Impacto costo', 'costImpact', config.costImpactOptions],
                      ['Resuelto', 'resolved', config.resolutionOptions],
                    ].map(([label, key, options]) => (
                      <label key={key} className="text-xs font-semibold uppercase text-slate-400">
                        {label}
                        <select
                          value={detail[key] || ''}
                          onChange={(event) => updateDiagnostic(index, { [key]: event.target.value })}
                          className="mt-1 block w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#004AAD]/25 focus:border-[#004AAD]/40 transition-all"
                        >
                          <option value="">Selecciona</option>
                          {options.map((option) => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Comment */}
                <label className="block text-xs font-semibold uppercase text-slate-400">
                  Comentario ejecutivo
                  <textarea
                    rows={2}
                    value={detail.comment || ''}
                    onChange={(event) => updateDiagnostic(index, { comment: event.target.value })}
                    className="mt-1 block w-full rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-2.5 text-sm text-slate-800 placeholder:italic placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#004AAD]/25 focus:border-[#004AAD]/40 transition-all"
                    placeholder="Ej. retrasos por roll-over, cobros no previstos, buena respuesta comercial..."
                  />
                </label>
              </div>
            </div>
          );
        })}
      </div>

      {error && <p className="text-rose-500 text-sm">{error}</p>}
    </div>
  );
};

export default NavieraDiagnostics;
